import { useConnectionState, useZero } from '@rocicorp/zero/react';
import { useCallback, useState } from 'react';

const PANEL_W = 220;

function getConnectionReason(
  connectionState: ReturnType<typeof useConnectionState>,
): string | undefined {
  const { name } = connectionState;
  if (
    name === 'disconnected' ||
    name === 'connecting' ||
    name === 'error' ||
    name === 'closed'
  ) {
    return connectionState.reason;
  }
  if (name === 'needs-auth') {
    const r = connectionState.reason;
    return r != null && typeof r === 'object' && 'reason' in r
      ? r.reason
      : r != null
        ? `${r.type} ${r.status}`
        : undefined;
  }
  return undefined;
}

interface ZeroCacheDebugPanelProps {
  /** Whether Zero is currently enabled (defaults to the LaunchDarkly flag value). */
  zeroEnabled: boolean;
  /** Flip the local Zero toggle for this session. */
  onToggleZero: () => void;
  /**
   * Whether Zero is actually mounted. The live connection section uses Zero
   * hooks, which only work inside `ZeroProvider`, so it is rendered only when
   * this is true.
   */
  showConnection: boolean;
}

export function ZeroCacheDebugPanel({
  zeroEnabled,
  onToggleZero,
  showConnection,
}: ZeroCacheDebugPanelProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Trigger tab — bottom right */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          position: 'fixed',
          bottom: 12,
          right: open ? PANEL_W : 0,
          zIndex: 99999,
          padding: '5px 7px',
          fontSize: 12,
          fontFamily: 'monospace',
          background: 'rgba(15, 15, 30, 0.85)',
          color: zeroEnabled ? '#4ade80' : '#94a3b8',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRight: open ? 'none' : undefined,
          borderRadius: '6px 0 0 6px',
          cursor: 'pointer',
          backdropFilter: 'blur(8px)',
          transition: 'right 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}
        title="Zero Cache Debug"
      >
        <WifiIcon connected={zeroEnabled} size={13} />
        <span style={{ fontSize: 9 }}>{open ? '>' : '<'}</span>
      </button>

      {/* Compact bottom-right drawer */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          right: open ? 0 : -PANEL_W,
          width: PANEL_W,
          zIndex: 99998,
          background: 'rgba(15, 15, 30, 0.9)',
          backdropFilter: 'blur(12px)',
          borderLeft: '1px solid rgba(255,255,255,0.1)',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '8px 0 0 0',
          color: '#e2e8f0',
          fontFamily: 'monospace',
          fontSize: 10,
          padding: '10px 12px 12px',
          transition: 'right 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>
          Zero Cache
        </span>

        {/* Local flag toggle — always available so Zero can be flipped on/off
            even while it is currently disabled. */}
        <ZeroFlagToggleRow enabled={zeroEnabled} onToggle={onToggleZero} />

        {/* Connection status + actions — only meaningful (and only able to use
            the Zero hooks) once Zero is mounted. */}
        {showConnection && <ZeroConnectionStatus />}
      </div>
    </>
  );
}

function ZeroFlagToggleRow({
  enabled,
  onToggle,
}: {
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <span style={{ color: '#94a3b8' }}>Zero (local)</span>
      <button
        role="switch"
        aria-checked={enabled}
        aria-label="Toggle Zero locally"
        onClick={onToggle}
        title="Enable/disable Zero for this session (resets to the feature-flag value on full reload)"
        style={{
          position: 'relative',
          width: 30,
          height: 16,
          borderRadius: 8,
          border: 'none',
          cursor: 'pointer',
          background: enabled ? '#4ade80' : 'rgba(255,255,255,0.2)',
          transition: 'background 0.15s ease',
          padding: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: enabled ? 16 : 2,
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: '#fff',
            transition: 'left 0.15s ease',
          }}
        />
      </button>
    </div>
  );
}

function ZeroConnectionStatus() {
  const connectionState = useConnectionState();
  const zero = useZero();

  const reason = getConnectionReason(connectionState);

  const handleReconnect = useCallback(() => {
    if (connectionState.name === 'needs-auth') {
      console.warn(
        '[Zero] Reconnect from needs-auth requires refreshed auth token — reload the page to re-authenticate',
      );
      return;
    }
    void zero.connection.connect();
  }, [zero, connectionState.name]);

  const handleClearIdb = useCallback(async () => {
    try {
      await zero.delete();
      window.location.reload();
    } catch (e) {
      console.error('[Zero] Failed to clear IDB:', e);
    }
  }, [zero]);

  return (
    <>
      {/* Action buttons */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 6,
        }}
      >
        <IconButton onClick={handleReconnect} title="Reconnect">
          {/* Refresh icon */}
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </IconButton>
        <IconButton onClick={handleClearIdb} title="Clear IDB & Reload">
          {/* Trash icon */}
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </IconButton>
      </div>

      {/* Connection status */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <StatusRow
          label="Status"
          value={connectionState.name}
          color={stateColor(connectionState.name)}
        />
        {reason && (
          <div style={{ color: '#f87171', wordBreak: 'break-word', fontSize: 9 }}>
            {String(reason)}
          </div>
        )}
      </div>
    </>
  );
}

function stateColor(name: string): string {
  switch (name) {
    case 'connected':
      return '#4ade80';
    case 'connecting':
      return '#facc15';
    case 'needs-auth':
    case 'error':
      return '#f87171';
    default:
      return '#94a3b8';
  }
}

function WifiIcon({ connected, size }: { connected: boolean; size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {connected ? (
        <>
          <path d="M5 12.55a11 11 0 0 1 14.08 0" />
          <path d="M1.42 9a16 16 0 0 1 21.16 0" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
        </>
      ) : (
        <>
          <line x1="1" y1="1" x2="23" y2="23" />
          <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
          <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
          <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
          <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
        </>
      )}
    </svg>
  );
}

function StatusRow({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1px 0',
      }}
    >
      <span style={{ color: '#94a3b8' }}>{label}</span>
      <span style={{ color, fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function IconButton({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        padding: 4,
        background: 'rgba(255,255,255,0.06)',
        color: '#94a3b8',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 4,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </button>
  );
}
